import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
// import { TranslateService } from '@ngx-translate/core';
import { ModalService, UsersService } from '@ops/shared-types';
import { Subscription } from 'rxjs';

interface EditedByDropDown {
  userId: string;
  date: Date;
}

@Component({
  selector: 'ops-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
})
export class DropdownComponent implements OnInit, OnDestroy {
  @Input() id: string;
  @Input() parentId: string;
  @Input() width: string;
  @Input() isMultiSelect: boolean;
  @Input() options: Array<string>;
  @Input() selectedValues: Array<string>;
  @Input() checkedImg: string;
  @Input() uncheckedImg: string;
  @Input() startingFocusIndex: number;
  @Input() paddingLeft: number;
  @Input() addNewOption?: boolean;
  @Input() optionSpacing: number;
  @Input() indent: number;
  @Input() position: { x: number; y: number };
  @Input() closeOnScroll: boolean = true;
  @Input() isOnTop?: boolean;

  @Input() ignoreSort?: boolean;

  @Input() customIndexes?: Array<number>;

  @Input() editedBy?: EditedByDropDown;

  @Output() valueSelected: EventEmitter<string> = new EventEmitter<string>();

  @ViewChildren('option', { read: ElementRef }) optionElements: QueryList<any>;
  @ViewChild('modalBackground') background: ElementRef;
  @ViewChild('dropdownWindow') dropdownWindow: ElementRef<HTMLDivElement>;

  //TODO: add option spacing as Input variable

  private element: HTMLElement | null;
  private isOpen = false;
  private currentFocusId: number;
  private justifiedFlag: boolean;

  editedByFullName?: string;

  constructor(
    private usersService: UsersService,
    private modalService: ModalService,
    private el: ElementRef
  ) {
    this.isOpen = false;
    this.element = this.el.nativeElement;
  }

  ngOnInit(): void {
    this.isOpen = false;
    if (!this.id) {
      console.error('modal must have an id');
      return;
    }

    if (!this.startingFocusIndex) {
      this.startingFocusIndex = 0;
    }
    if (!this.selectedValues) {
      this.selectedValues = [];
    }

    const parent = document.querySelector(`#${this.parentId}`);
    if (this.element && parent) parent.appendChild(this.element);
    this.modalService.add(this);

    if (this.element) {
      this.element.style.width = this.width;
      this.element.style.display = 'none';
    }
    this.currentFocusId = this.startingFocusIndex;
  }

  ngOnDestroy(): void {
    if (this.subUserEditedBy?.unsubscribe) {
      this.subUserEditedBy.unsubscribe();
    }

    this.modalService.remove(this.id);
    this.element?.remove();
    clearTimeout(this.blurTimeout);
    this.isOpen = false;
  }

  private ensureAttachedToParent(): void {
    const parent = document.querySelector(`#${this.parentId}`);
    if (this.element && parent && !parent.contains(this.element)) {
      parent.appendChild(this.element);
    }
  }

  get optionItems() {
    return this.options?.filter(
      (value) => value?.toUpperCase()?.includes('DELETE') === false
    );
  }

  get deleteOption() {
    const deleteOptionMatch = this.options?.filter((value) =>
      value?.toUpperCase()?.includes('DELETE')
    );

    return deleteOptionMatch?.length ? deleteOptionMatch[0] : '';
  }

  subUserEditedBy?: Subscription;

  async getAndSetUserEditedBy() {
    this.subUserEditedBy = await this.usersService
      .getUserById(this.editedBy.userId)
      .subscribe((userFullName) => {
        this.editedByFullName = userFullName;
      });
    // console.log('FETCH USER_BY_ID');
  }

  open(): void {
    // Ensure dropdown is properly attached before opening
    this.ensureAttachedToParent();

    if (this.optionSpacing != null) {
      this.optionElements.forEach((e) => {
        e.nativeElement.style.marginTop = this.optionSpacing + 'px';
      });
    }
    if (this.indent) {
      this.optionElements.forEach((e) => {
        e.nativeElement.style.marginLeft = this.indent + 'px';
      });
    }
    if (this.position) {
      this.dropdownWindow.nativeElement.style.position = 'fixed';
      this.dropdownWindow.nativeElement.style.top = this.position.y + 'px';
      this.dropdownWindow.nativeElement.style.left = this.position.x + 'px';
      // console.log(this.position);
    }

    if (this.element) {
      this.element.style.display = 'flex';
      this.element.style.position = 'absolute';
      this.element.style.top = '0';
      this.element.focus();
    }

    this.isOpen = true;

    if (this.editedBy?.userId) {
      this.getAndSetUserEditedBy();
    }

    if (!this.justifiedFlag && this.checkedImg) {
      this.optionElements.forEach((el) => {
        el.nativeElement.style.textIndent = '0px';
      });
      this.justifiedFlag = true;
      return;
    }
    if (!this.justifiedFlag && this.paddingLeft) {
      this.optionElements.forEach((el) => {
        el.nativeElement.style.paddingLeft = this.paddingLeft.toString() + 'px';
        el.nativeElement.style.textIndent = '0px';
        let tempWidth = el.nativeElement.offsetWidth - this.paddingLeft * 2;
        el.nativeElement.style.width = tempWidth + 'px';
      });
      this.paddingLeft = 0;
    }
  }

  close(): void {
    document
      ?.getElementById(this.currentFocusId?.toString())
      ?.classList?.remove('selected');
    this.currentFocusId = this.startingFocusIndex;
    if (this.element) this.element.style.display = 'none';
    this.isOpen = false;
  }

  onAddNewOption(event: any, value: string): void {
    event?.stopPropagation();
    this.valueSelected.emit(value);
    this.close();
  }

  onSelectValue(event: any, value: string): void {
    event?.stopPropagation();
    this.valueSelected.emit(value);
    if (this.isMultiSelect === true) {
      this.toggleSelection(value);
      return;
    }
    this.selectValue(value);
    this.close();
  }

  onKeypress(key: any, option: string) {
    if (key.keyCode !== 32 && key.keyCode !== 13) {
      //enter and space keys
      return;
    }
    this.onSelectValue(undefined, option);
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: any) {
    // console.log("E => ", e);
    if (
      e?.target?.classList?.contains('kebab-menu') === false &&
      e?.target?.parentElement?.classList?.contains('kebab-menu') === false &&
      !this.element.parentElement.contains(e.target)
    ) {
      if (!this.isOpen) return;
      this.close();
    } else if (
      (e?.target?.classList?.contains('kebab-menu') !== false ||
        e?.target?.parentElement?.classList?.contains('kebab-menu') !==
          false) &&
      !this?.element?.parentElement?.contains(e?.target)
    ) {
      if (!this.isOpen) return;
      this.close();
    }
  }

  @HostListener('document:keyup', ['$event'])
  onKeyUp(e: any) {
    if (!this.isOpen) return;
    if (e.keyCode === 9) {
      this.close();
      return;
    }
    e.preventDefault();
    switch (e.keyCode) {
      case 38: {
        //UP arrow
        document
          .getElementById(this.currentFocusId.toString())
          ?.classList.remove('selected');
        if (this.currentFocusId - this.startingFocusIndex > 1) {
          this.currentFocusId--;
        } else {
          this.currentFocusId = this.startingFocusIndex + this.options.length;
        }
        let element = document.getElementById(this.currentFocusId.toString());
        element?.focus();
        element?.classList.add('selected');
        break;
      }
      case 40: {
        //DOWN arrow
        document
          .getElementById(this.currentFocusId.toString())
          ?.classList.remove('selected');
        if (
          this.startingFocusIndex + this.options.length - this.currentFocusId >
          0
        ) {
          this.currentFocusId++;
        } else {
          this.currentFocusId = this.startingFocusIndex + 1;
        }
        let element = document.getElementById(this.currentFocusId.toString());
        element?.focus();
        element?.classList.add('selected');
        break;
      }
      case 27:
        this.close();
        break;
    }
  }

  blurTimeout;

  focus(event: any) {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }

    event.target.focus();
  }

  blur(event: any) {
    this.blurTimeout = setTimeout(() => {
      event.target.blur();
      this.close();
      clearTimeout(this.blurTimeout);
    }, 3000);
  }

  closeFromBackground(event: any) {
    // console.log('CLOSE');
    clearTimeout(this.blurTimeout);
    event.stopPropagation();
    this.close();
  }

  getCustomClassForIndex(optionIndex: number) {
    if (
      this.customIndexes?.length > 0 &&
      this.customIndexes?.includes(optionIndex)
    ) {
      return 'no-action-no-wrap';
    }
    return '';
  }

  private toggleSelection(value: string) {
    const existing =
      this.selectedValues.length > 0 && this.selectedValues.includes(value);
    if (existing) {
      this.selectedValues = this.selectedValues.filter((v) => v !== value);
      return;
    }
    this.selectedValues.push(value);
  }

  private selectValue(value: string): void {
    if (this.selectedValues && this.selectedValues.includes(value)) {
      this.selectedValues = [];
      return;
    }
    this.selectedValues = [value];
  }
}
